import type {
  AppSpecFull,
  StudioActionFlowOntologyPlanTrace,
  StudioOntologyCompilerContract,
  StudioOntologyRetrievalItem,
  StudioOntologyRetrievalMode,
  StudioOntologyRetrievalTrace,
} from '@/services/block-api-service'
import {
  planActionFlowOntology,
  type ActionFlowOntologyPlanningResult,
} from '@cogo/headless-core/actionflow-studio-readiness'
import {
  buildDefaultOntologyMatchingAssets,
  buildOntologyMatchingPlan,
  type OntologyMatchingAsset,
  type OntologyMatchingAssetKind,
  type OntologyIntentChunk,
  type OntologyMatchingPlan,
} from '@cogo/headless-core/application-ontology-matching'
import {
  buildGeneratedUiVerificationPolicy,
  normalizeGeneratedUiProvenanceTier,
  validateGeneratedUiVerificationSurface,
  type GeneratedUiVerificationPolicy,
  type GeneratedUiVerificationSurface,
  type ImageSourceEvidence,
} from '@cogo/headless-core/image-evidence-verification'

export const STUDIO_EXPECTED_EMBEDDING_MODEL = 'dragonkue/BGE-m3-ko'
export const STUDIO_EXPECTED_EMBEDDING_DIMENSION = 1024

export type StudioOntologyRetrievalIssueCode =
  | 'missing-ontology-retrieval-trace'
  | 'actionflow-ontology-gap'
  | 'vector-dimension-mismatch'
  | 'vector-model-mismatch'
  | 'vector-used-without-candidates'
  | 'vector-readback-missing'
  | 'vector-only-hit-admitted'
  | 'structural-candidates-missing'
  | 'admitted-candidates-missing'
  | 'readback-missing'
  | 'ontology-matching-plan-missing'
  | 'ontology-matching-plan-blocked'
  | 'ontology-matching-actionflow-validation-missing'
  | 'ontology-compiler-contract-missing'
  | 'ontology-compiler-contract-blocked'
  | 'ontology-compiler-contract-authority-mismatch'
  | 'ontology-compiler-contract-hard-constraint-missing'
  | 'image-source-evidence-promotion-mismatch'
  | 'image-source-hidden-truth-inferred'
  | 'generated-ui-verification-policy-missing'
  | 'generated-ui-verification-policy-mismatch'
  | 'generated-ui-verification-blocked'

export interface StudioOntologyRetrievalIssue {
  code: StudioOntologyRetrievalIssueCode
  severity: 'error' | 'warning'
  message: string
}

export interface StudioOntologyRetrievalValidation {
  valid: boolean
  issues: StudioOntologyRetrievalIssue[]
}

export interface BuildStudioOntologyRetrievalTraceOptions {
  /**
   * What the PERSON submitted.
   *
   * Without it the ActionFlow plan is derived from `appSpec.description` —
   * text the decompose stage wrote downstream of the request — so the only
   * evidence left for admission is the appSpec's own blocks and binds, and the
   * pipeline ends up vouching for itself. Callers that hold the submission
   * pass it; `buildActionFlowOntologyPlanTrace` states what it falls back to
   * when they do not.
   */
  prompt?: string
  /**
   * Scenario ids decompose already admitted (`ontology-fast-path.ts`
   * `https://cogo.xyz/ns/v1/actionflow-archetype#${admittedScenarioId}`).
   * Without them the client re-plans from prompt+blocks and can gap a
   * subject the wire already named.
   */
  requestedScenarioIds?: readonly string[]
  source?: string
  generatedAt?: string
  mode?: StudioOntologyRetrievalMode
  fallbackReason?: string
  preserveExistingSource?: boolean
}

interface RemoteTraceLike {
  source?: string
  generatedAt?: string
  mode?: string
  embeddingUsed?: boolean
  embeddingModel?: string
  embeddingDimension?: number
  embeddingSimilarityTopK?: number
  structuralCandidateCount?: number
  vectorCandidateCount?: number
  admittedCandidateCount?: number
  selectedOntologySubject?: string
  selectedOntologySubjects?: string[]
  readbackPassed?: boolean
  readbackSubjectIds?: string[]
  fallbackUsed?: boolean
  retrievalFallbackReason?: string
  reason?: string
  vectorOnlyHitRejected?: boolean
  lowSimilarityFallback?: boolean
  items?: unknown
  actionFlowOntologyPlan?: unknown
  ontologyMatchingPlan?: OntologyMatchingPlan
  ontologyCompilerContract?: unknown
  imageSourceEvidence?: unknown
  generatedUiVerificationSurface?: unknown
  generatedUiVerificationPolicy?: unknown
}

function stableId(value: string | undefined, fallback: string): string {
  const normalized = (value ?? fallback).trim()
  return normalized || fallback
}

function ontologySubjectsFromAppSpec(appSpec: AppSpecFull): string[] {
  const refs = [
    ...(appSpec.generationResponsibilities ?? []).flatMap((decision) => decision.ontologyRefs),
    ...(appSpec.goldenRecipePack?.recipes ?? []).flatMap((recipe) => [`recipe:${recipe.id}`]),
  ]
  return [...new Set(refs.filter((ref) => ref.trim().length > 0))]
}

function itemsFromAppSpec(appSpec: AppSpecFull): StudioOntologyRetrievalItem[] {
  const decisions = appSpec.generationResponsibilities ?? []
  if (decisions.length === 0) {
    return appSpec.blocks.map((block, index) => ({
      assetId: `block:${stableId(block.name, `block-${index + 1}`)}`,
      kind: 'component',
      sourceRef: `appSpec.blocks.${block.name || index}`,
      ontologySubject: `uiox:ComponentType/${block.type}`,
      evidenceRefs: [`appSpec.blocks.${block.name || index}`],
    }))
  }

  return decisions.map((decision) => ({
    assetId: decision.partId,
    kind: decision.partKind,
    sourceRef: decision.selectedRecipeRef ?? decision.ontologyRefs[0] ?? decision.partId,
    ontologySubject: decision.ontologyRefs[0] ?? decision.selectedRecipeRef ?? decision.partId,
    evidenceRefs: decision.evidenceRefs,
  }))
}


const ACTIONFLOW_ARCHETYPE_SUBJECT_PREFIX =
  'https://cogo.xyz/ns/v1/actionflow-archetype#'

function requestedScenarioIdsFromActionFlowArchetypeSubjects(
  subjects: readonly string[],
): string[] {
  return [...new Set(
    subjects
      .filter((subject) => subject.startsWith(ACTIONFLOW_ARCHETYPE_SUBJECT_PREFIX))
      .map((subject) => subject.slice(ACTIONFLOW_ARCHETYPE_SUBJECT_PREFIX.length))
      .filter((id) => id.length > 0),
  )]
}

function hasActionFlowOntologyNeed(appSpec: AppSpecFull): boolean {
  return (
    appSpec.interactionContract?.actionFlowRequired === true ||
    appSpec.blocks.some((block) => (block.binds ?? []).length > 0) ||
    Boolean(appSpec.actionFlowTopology?.primitives.some((primitive) => primitive.required))
  )
}

function buildActionFlowOntologyPlanTrace(
  appSpec: AppSpecFull,
  prompt?: string,
  requestedScenarioIds?: readonly string[],
): StudioActionFlowOntologyPlanTrace | undefined {
  if (!hasActionFlowOntologyNeed(appSpec)) return undefined
  const result = planActionFlowOntology({
    // The submission when the caller has it. `appSpec.description` is the
    // fallback and it is a weak one — generated text, scored as though the
    // person had written it. It stays only so a caller that cannot reach the
    // submission still produces a trace rather than no trace at all.
    prompt: prompt ?? appSpec.description,
    appName: appSpec.name,
    description: appSpec.description,
    blockTypes: appSpec.blocks.map((block) => block.type),
    blockNames: appSpec.blocks.map((block) => block.name),
    binds: appSpec.blocks.flatMap((block) => block.binds ?? []),
    datasourceNames: (appSpec.datasources ?? []).map((datasource) => datasource.name),
    ...(requestedScenarioIds && requestedScenarioIds.length > 0
      ? { requestedScenarioIds }
      : {}),
  })
  return toStudioActionFlowOntologyPlanTrace(result)
}

function toStudioActionFlowOntologyPlanTrace(
  result: ActionFlowOntologyPlanningResult,
): StudioActionFlowOntologyPlanTrace {
  return {
    schemaVersion: result.schemaVersion,
    contractVersion: result.contractVersion,
    generatedAt: result.generatedAt,
    status: result.status,
    valid: result.valid,
    publishBlocked: result.publishBlocked,
    selectedScenarioIds: result.selectedScenarioIds,
    selectedArchetypes: result.selectedArchetypes,
    selectedOntologySubjects: result.selectedOntologySubjects,
    coverage: result.coverage,
    gaps: result.gaps,
    evidenceRefs: result.evidenceRefs,
  }
}

function buildOntologyIntentChunks(appSpec: AppSpecFull): OntologyIntentChunk[] {
  const chunks: OntologyIntentChunk[] = []
  if (appSpec.description) {
    chunks.push({
      id: 'intent:description',
      text: appSpec.description,
      weight: 2,
    })
  }
  for (const page of appSpec.pages ?? []) {
    chunks.push({
      id: `page:${stableId(page.id, page.route)}`,
      text: [page.title, page.route, ...(page.blocks ?? [])].filter(Boolean).join(' '),
      weight: 1.4,
      tags: ['page'],
    })
  }
  for (const block of appSpec.blocks ?? []) {
    chunks.push({
      id: `block:${stableId(block.name, block.type)}`,
      text: [block.name, block.type, block.description, ...(block.binds ?? [])].filter(Boolean).join(' '),
      weight: 1.2,
      tags: ['ui-block', block.type],
    })
  }
  return chunks.length > 0
    ? chunks
    : [{ id: 'intent:name', text: appSpec.name, weight: 1 }]
}

function matchingAssetKindFromRetrievalKind(kind: string | undefined): OntologyMatchingAssetKind | null {
  const normalized = kind?.trim().toLowerCase()
  if (normalized === 'page') return 'page'
  if (normalized === 'block' || normalized === 'ui-block' || normalized === 'section') return 'ui-block'
  if (normalized === 'component' || normalized === 'element') return 'component'
  return null
}

function normalizeRetrievalItem(value: unknown): StudioOntologyRetrievalItem | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const record = value as Record<string, unknown>
  const evidenceRefs = stringArray(record.evidenceRefs)
  const item: StudioOntologyRetrievalItem = {
    evidenceRefs,
  }
  if (typeof record.assetId === 'string') item.assetId = record.assetId
  if (typeof record.kind === 'string') item.kind = record.kind
  if (typeof record.sourceRef === 'string') item.sourceRef = record.sourceRef
  if (typeof record.ontologySubject === 'string') item.ontologySubject = record.ontologySubject
  if (typeof record.chunkId === 'string') item.chunkId = record.chunkId
  if (typeof record.score === 'number') item.score = record.score
  if (typeof record.vectorSimilarity === 'number') item.vectorSimilarity = record.vectorSimilarity
  const tags = stringArray(record.tags)
  if (tags.length > 0) item.tags = tags
  const designSystemIds = stringArray(record.designSystemIds)
  if (designSystemIds.length > 0) item.designSystemIds = designSystemIds
  const templateIds = stringArray(record.templateIds)
  if (templateIds.length > 0) item.templateIds = templateIds
  const requiredChildIds = stringArray(record.requiredChildIds)
  if (requiredChildIds.length > 0) item.requiredChildIds = requiredChildIds
  const optionalChildIds = stringArray(record.optionalChildIds)
  if (optionalChildIds.length > 0) item.optionalChildIds = optionalChildIds
  const requiredComponents = stringArray(record.requiredComponents)
  if (requiredComponents.length > 0) item.requiredComponents = requiredComponents
  const actionOutputs = stringArray(record.actionOutputs)
  if (actionOutputs.length > 0) item.actionOutputs = actionOutputs
  const dataInputs = stringArray(record.dataInputs)
  if (dataInputs.length > 0) item.dataInputs = dataInputs
  return item
}

function normalizeRetrievalItems(value: unknown): StudioOntologyRetrievalItem[] {
  return Array.isArray(value)
    ? value.flatMap((item) => {
        const normalized = normalizeRetrievalItem(item)
        return normalized ? [normalized] : []
      })
    : []
}

function matchingAssetsFromRetrievalItems(
  items: readonly StudioOntologyRetrievalItem[],
): OntologyMatchingAsset[] {
  const assets = new Map<string, OntologyMatchingAsset>()
  for (const item of items) {
    const kind = matchingAssetKindFromRetrievalKind(item.kind)
    if (!kind) continue
    const id = stableId(
      item.assetId ?? item.ontologySubject ?? item.sourceRef ?? item.chunkId,
      `${kind}:${assets.size + 1}`,
    )
    if (assets.has(id)) continue
    const evidenceRefs = item.evidenceRefs.length > 0
      ? item.evidenceRefs
      : [item.sourceRef, item.ontologySubject].filter((ref): ref is string => Boolean(ref))
    assets.set(id, {
      id,
      kind,
      label: item.sourceRef ?? item.ontologySubject ?? id,
      description: [
        item.sourceRef,
        item.ontologySubject,
        typeof item.score === 'number' ? `score:${item.score}` : undefined,
      ].filter(Boolean).join(' '),
      tags: [
        item.kind,
        item.sourceRef,
        item.ontologySubject,
        ...(item.tags ?? []),
      ].filter((tag): tag is string => Boolean(tag)),
      ontologyRefs: [
        item.ontologySubject,
        ...evidenceRefs,
      ].filter((ref): ref is string => Boolean(ref)),
      chunkHandles: item.chunkId ? [item.chunkId] : undefined,
      designSystemIds: item.designSystemIds,
      templateIds: item.templateIds,
      requiredChildIds: item.requiredChildIds,
      optionalChildIds: item.optionalChildIds,
      requiredComponents: item.requiredComponents,
      actionOutputs: item.actionOutputs,
      dataInputs: item.dataInputs,
    })
  }
  return [...assets.values()]
}

function buildOntologyMatchingPlanTrace(
  appSpec: AppSpecFull,
  retrievalItems: readonly StudioOntologyRetrievalItem[] = [],
): OntologyMatchingPlan {
  const retrievedAssets = matchingAssetsFromRetrievalItems(retrievalItems)
  const assets = [
    ...retrievedAssets,
    ...buildDefaultOntologyMatchingAssets(),
  ].filter((asset, index, entries) => (
    entries.findIndex((entry) => entry.kind === asset.kind && entry.id === asset.id) === index
  ))
  const requiredPageIds = retrievedAssets.filter((asset) => asset.kind === 'page').map((asset) => asset.id)
  const retrievedUiBlockIds = retrievedAssets.filter((asset) => asset.kind === 'ui-block').map((asset) => asset.id)
  const inferredUiBlockIds = inferCanonicalUiBlockIds(appSpec)
  const pageRequiredUiBlockIds = requiredPageIds.flatMap(
    (pageId) => assets.find((asset) => asset.kind === 'page' && asset.id === pageId)?.requiredChildIds ?? [],
  )
  const initialRequiredUiBlockIds = [...new Set([
    ...retrievedUiBlockIds,
    ...inferredUiBlockIds,
    ...pageRequiredUiBlockIds,
  ])]
  const retrievedComponentIds = retrievedAssets.filter((asset) => asset.kind === 'component').map((asset) => asset.id)
  const canonicalComponentIds = new Set(
    assets.filter((asset) => asset.kind === 'component').map((asset) => asset.id),
  )
  const inferredComponentIds = appSpec.blocks
    .map((block) => block.type.trim().toLowerCase().replace(/-/g, '_'))
    .filter((type) => canonicalComponentIds.has(type))
  const preliminaryPlan = buildOntologyMatchingPlan({
    intent: [appSpec.name, appSpec.description].filter(Boolean).join(' '),
    chunks: buildOntologyIntentChunks(appSpec),
    assets,
    selected: {
      designSystemId: appSpec.projectPlan?.designSystemId ?? undefined,
      templateId: appSpec.projectPlan?.templateId ?? undefined,
      ...(requiredPageIds.length > 0 ? { requiredPageIds } : {}),
      ...(initialRequiredUiBlockIds.length > 0
        ? { requiredUiBlockIds: initialRequiredUiBlockIds }
        : {}),
      ...(inferredComponentIds.length > 0
        ? { requiredComponentIds: inferredComponentIds }
        : {}),
    },
  })
  const matchedUiBlockIds = preliminaryPlan.decisions
    .filter((decision) => decision.kind === 'ui-block' && decision.decision !== 'create_candidate')
    .map((decision) => decision.requirementId)
  const requiredUiBlockIds = [...new Set([...initialRequiredUiBlockIds, ...matchedUiBlockIds])]
  const uiBlockRequiredComponentIds = requiredUiBlockIds.flatMap(
    (blockId) => assets.find((asset) => asset.kind === 'ui-block' && asset.id === blockId)?.requiredComponents ?? [],
  )
  const requiredComponentIds = [...new Set([
    ...retrievedComponentIds,
    ...inferredComponentIds,
    ...uiBlockRequiredComponentIds,
  ])]
  return buildOntologyMatchingPlan({
    intent: [appSpec.name, appSpec.description].filter(Boolean).join(' '),
    chunks: buildOntologyIntentChunks(appSpec),
    assets,
    selected: {
      designSystemId: appSpec.projectPlan?.designSystemId ?? undefined,
      templateId: appSpec.projectPlan?.templateId ?? undefined,
      ...(requiredPageIds.length > 0 ? { requiredPageIds } : {}),
      ...(requiredUiBlockIds.length > 0 ? { requiredUiBlockIds } : {}),
      ...(requiredComponentIds.length > 0 ? { requiredComponentIds } : {}),
    },
  })
}

function inferCanonicalUiBlockIds(appSpec: AppSpecFull): string[] {
  const matches = new Set<string>()
  for (const block of appSpec.blocks) {
    const text = [block.name, block.type, block.description]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    if (/\b(filter|search|query)\b/.test(text)) matches.add('filter-toolbar')
    if (/\b(export|download|artifact)\b/.test(text)) matches.add('document-export-panel')
    if (/\b(chart|aggregate|analytics?)\b/.test(text) && /\b(table|rows?|records?)\b/.test(text)) {
      matches.add('chart-table-composition')
    }
    if (/\b(table|grid|rows?|records?)\b/.test(text) && /\b(action|approve|update|select|bulk|status)\b/.test(text)) {
      matches.add('data-grid-with-actions')
    }
    if (/\b(list|feed|items?|records?)\b/.test(text)) matches.add('content-list')
    if (/\b(detail|summary|selected|result)\b/.test(text)) matches.add('detail-summary')
    if (/\b(navigation|sidebar|nav)\b/.test(text)) matches.add('sidebar-navigation')
    if (/\b(command|quick action|global search)\b/.test(text)) matches.add('command-header')
  }
  return [...matches]
}

function sourceToCompilerEntrypoint(source: string | undefined): 'chat' | 'template' | 'import' | 'simple-edit' | 'publish' {
  const normalized = (source ?? '').toLowerCase()
  if (normalized.includes('template')) return 'template'
  if (normalized.includes('import')) return 'import'
  if (normalized.includes('edit') || normalized.includes('patch')) return 'simple-edit'
  if (normalized.includes('publish')) return 'publish'
  return 'chat'
}

function buildStudioOntologyCompilerContract(
  appSpec: AppSpecFull,
  plan: OntologyMatchingPlan,
  options: BuildStudioOntologyRetrievalTraceOptions = {},
): StudioOntologyCompilerContract {
  const selectedDesignSystemId = appSpec.projectPlan?.designSystemId ?? undefined
  const selectedTemplateId = appSpec.projectPlan?.templateId ?? undefined
  const actionFlowRequiredValidation = plan.actionFlowImpact?.requiredValidation ?? []
  const blockedDecisions = plan.decisions.filter((decision) => decision.generationAllowed === false)
  const hardConstraintSet = new Set(plan.hardConstraints)
  const reasons: string[] = []

  if (selectedDesignSystemId && !hardConstraintSet.has(`design-system:${selectedDesignSystemId}:hard`)) {
    reasons.push(`Selected design system ${selectedDesignSystemId} is not present in hard constraints.`)
  }
  if (selectedTemplateId && !hardConstraintSet.has(`template:${selectedTemplateId}:hard`)) {
    reasons.push(`Selected template ${selectedTemplateId} is not present in hard constraints.`)
  }
  if (blockedDecisions.length > 0) {
    reasons.push(
      `Candidate-only ontology gaps block publish: ${blockedDecisions.map((decision) => `${decision.kind}:${decision.requirementId}`).join(', ')}.`,
    )
  }

  const activeEntrypoint = sourceToCompilerEntrypoint(options.source)
  const entrypoints: StudioOntologyCompilerContract['entrypoints'] = [
    {
      entrypoint: 'chat',
      enforcement: activeEntrypoint === 'chat' ? 'tool-compiler' : 'publish-gate',
      evidenceRefs: ['appSpec.ontologyRetrievalTrace.ontologyMatchingPlan'],
    },
    {
      entrypoint: 'template',
      enforcement: selectedTemplateId ? 'tool-compiler' : 'not-selected',
      evidenceRefs: selectedTemplateId
        ? [`projectPlan.templateId:${selectedTemplateId}`, 'ontologyMatchingPlan.hardConstraints']
        : [],
    },
    {
      entrypoint: 'import',
      enforcement: activeEntrypoint === 'import' ? 'tool-compiler' : 'publish-gate',
      evidenceRefs: ['evaluateStudioPublishReadiness.ontologyMatchingReadinessGate'],
    },
    {
      entrypoint: 'simple-edit',
      enforcement: activeEntrypoint === 'simple-edit' ? 'tool-compiler' : 'publish-gate',
      evidenceRefs: ['evaluateStudioPublishReadiness.ontologyMatchingReadinessGate'],
    },
    {
      entrypoint: 'publish',
      enforcement: 'publish-gate',
      evidenceRefs: ['evaluateStudioPublishReadiness.ontologyMatchingReadinessGate'],
    },
  ]

  return {
    schemaVersion: 'studio-ontology-compiler-contract.v1',
    authority: 'tool',
    status: reasons.length === 0 ? 'ready' : 'blocked',
    ...(selectedDesignSystemId ? { selectedDesignSystemId } : {}),
    ...(selectedTemplateId ? { selectedTemplateId } : {}),
    hardConstraints: plan.hardConstraints,
    entrypoints,
    fallbackLadder: [
      { from: 'page', to: 'ui-block', generationAllowed: true },
      { from: 'ui-block', to: 'component', generationAllowed: true },
      { from: 'component', to: 'candidate-queue', generationAllowed: false },
    ],
    actionFlowMutationPolicy: {
      failClosed: true,
      requiredValidation: actionFlowRequiredValidation.length > 0
        ? [...actionFlowRequiredValidation]
        : [
            'interaction-contract-recalculation',
            'compilerParamBindings',
            'validateActionFlowTopologyContract',
            'runtime-simulation',
            'browser-trace',
          ],
    },
    llmPolicy: {
      role: 'advisory-only',
      cannotOverrideHardConstraints: true,
    },
    candidatePolicy: {
      publishCandidateDirectly: false,
      promotionRequires: [
        'repeatable-generation-proof',
        'component-vocabulary-stability',
        'design-system-token-stability',
        'render-proof',
        'actionflow-proof',
      ],
    },
    evidenceRefs: [
      'appSpec.ontologyRetrievalTrace.ontologyMatchingPlan',
      'packages/cogo-headless/src/application-ontology-matching.ts',
      '@cogo/headless-core.buildOntologyMatchingPlan',
      ...plan.decisions.flatMap((decision) => decision.evidenceRefs),
    ],
    reasons,
  }
}

function isStudioOntologyCompilerContract(value: unknown): value is StudioOntologyCompilerContract {
  return Boolean(
    value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      (value as Record<string, unknown>).schemaVersion === 'studio-ontology-compiler-contract.v1',
  )
}

function isActionFlowOntologyPlanTrace(
  value: unknown,
): value is StudioActionFlowOntologyPlanTrace {
  return Boolean(
    value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      (value as Record<string, unknown>).schemaVersion ===
        'actionflow-ontology-planning-result.v1',
  )
}

function isImageSourceEvidence(value: unknown): value is ImageSourceEvidence {
  return Boolean(
    value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      (value as Record<string, unknown>).schemaVersion === 'cogo.image-source-evidence.v1',
  )
}

function isGeneratedUiVerificationSurface(
  value: unknown,
): value is GeneratedUiVerificationSurface {
  return Boolean(
    value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      typeof (value as Record<string, unknown>).id === 'string' &&
      typeof (value as Record<string, unknown>).provenanceTier === 'string',
  )
}

function isGeneratedUiVerificationPolicy(
  value: unknown,
): value is GeneratedUiVerificationPolicy {
  return Boolean(
    value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      (value as Record<string, unknown>).schemaVersion === 'cogo.generated-ui-verification-policy.v1',
  )
}

export function buildStudioOntologyRetrievalTrace(
  appSpec: AppSpecFull,
  options: BuildStudioOntologyRetrievalTraceOptions = {},
): StudioOntologyRetrievalTrace {
  const items = itemsFromAppSpec(appSpec)
  const subjects = ontologySubjectsFromAppSpec(appSpec)
  const actionFlowOntologyPlan = buildActionFlowOntologyPlanTrace(appSpec, options.prompt, options.requestedScenarioIds)
  const actionFlowSubjects = actionFlowOntologyPlan?.selectedOntologySubjects ?? []
  const readbackSubjectIds = subjects.length > 0
    ? subjects
    : items.map((item) => item.ontologySubject).filter((subject): subject is string => Boolean(subject))
  const mergedReadbackSubjectIds = [...new Set([...readbackSubjectIds, ...actionFlowSubjects])]
  const structuralCandidateCount = Math.max(items.length + actionFlowSubjects.length, mergedReadbackSubjectIds.length)
  const actionFlowItems: StudioOntologyRetrievalItem[] = actionFlowOntologyPlan
    ? actionFlowOntologyPlan.selectedOntologySubjects.map((subject, index) => ({
        assetId: `actionflow-archetype:${actionFlowOntologyPlan.selectedScenarioIds[index] ?? index + 1}`,
        kind: 'actionflow-archetype',
        sourceRef: actionFlowOntologyPlan.selectedScenarioIds[index] ?? subject,
        ontologySubject: subject,
        score: actionFlowOntologyPlan.status === 'matched' ? 1 : 0.5,
        evidenceRefs: actionFlowOntologyPlan.evidenceRefs,
      }))
    : []
  const allItems = [...items, ...actionFlowItems]
  const ontologyMatchingPlan = buildOntologyMatchingPlanTrace(appSpec, allItems)
  const ontologyCompilerContract = buildStudioOntologyCompilerContract(
    appSpec,
    ontologyMatchingPlan,
    options,
  )

  return {
    schemaVersion: 'studio-ontology-retrieval-trace.v1',
    source: options.source ?? 'studio-planner-structural',
    generatedAt: options.generatedAt ?? new Date().toISOString(),
    mode: options.mode ?? (options.fallbackReason ? 'vector-unavailable' : 'structural-only'),
    embeddingUsed: false,
    embeddingSimilarityTopK: 0,
    structuralCandidateCount,
    vectorCandidateCount: 0,
    admittedCandidateCount: mergedReadbackSubjectIds.length,
    selectedOntologySubject: mergedReadbackSubjectIds[0],
    selectedOntologySubjects: mergedReadbackSubjectIds,
    readbackPassed: mergedReadbackSubjectIds.length > 0,
    readbackSubjectIds: mergedReadbackSubjectIds,
    fallbackUsed: Boolean(options.fallbackReason),
    ...(options.fallbackReason ? { retrievalFallbackReason: options.fallbackReason } : {}),
    vectorOnlyHitRejected: false,
    lowSimilarityFallback: false,
    items: allItems,
    ...(actionFlowOntologyPlan ? { actionFlowOntologyPlan } : {}),
    ontologyMatchingPlan,
    ontologyCompilerContract,
  }
}

function isMode(value: string | undefined): value is StudioOntologyRetrievalMode {
  return value === 'facet-sparql'
    || value === 'lexical-sparql'
    || value === 'ontology-fast-path'
    || value === 'structural-only'
    || value === 'vector-rerank'
    || value === 'vector-unavailable'
    || value === 'low-similarity-fallback'
    || value === 'llm-rerank'
    || value === 'none'
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : []
}

export function normalizeStudioOntologyRetrievalTrace(
  remoteTrace: RemoteTraceLike | undefined,
  appSpec: AppSpecFull,
  options: BuildStudioOntologyRetrievalTraceOptions = {},
): StudioOntologyRetrievalTrace {
  if (!remoteTrace) return buildStudioOntologyRetrievalTrace(appSpec, options)

  const selectedOntologySubjects = stringArray(remoteTrace.selectedOntologySubjects)
  const requestedScenarioIds = requestedScenarioIdsFromActionFlowArchetypeSubjects([
    ...(typeof remoteTrace.selectedOntologySubject === 'string'
      ? [remoteTrace.selectedOntologySubject]
      : []),
    ...selectedOntologySubjects,
    ...stringArray(remoteTrace.readbackSubjectIds),
  ])
  const fallback = buildStudioOntologyRetrievalTrace(appSpec, {
    ...options,
    ...(requestedScenarioIds.length > 0 ? { requestedScenarioIds } : {}),
  })
  const readbackSubjectIds = stringArray(remoteTrace.readbackSubjectIds)
  const selectedOntologySubject = remoteTrace.selectedOntologySubject
    ?? selectedOntologySubjects[0]
    ?? readbackSubjectIds[0]
    ?? fallback.selectedOntologySubject
  const admittedCandidateCount = remoteTrace.admittedCandidateCount
    ?? (readbackSubjectIds.length || selectedOntologySubjects.length || fallback.admittedCandidateCount)
  const actionFlowOntologyPlan = isActionFlowOntologyPlanTrace(remoteTrace.actionFlowOntologyPlan)
    ? remoteTrace.actionFlowOntologyPlan
    : fallback.actionFlowOntologyPlan
  const items = normalizeRetrievalItems(remoteTrace.items)
  const ontologyMatchingPlan = remoteTrace.ontologyMatchingPlan
    ?? buildOntologyMatchingPlanTrace(appSpec, items.length > 0 ? items : fallback.items)
  const ontologyCompilerContract = isStudioOntologyCompilerContract(remoteTrace.ontologyCompilerContract)
    ? remoteTrace.ontologyCompilerContract
    : buildStudioOntologyCompilerContract(appSpec, ontologyMatchingPlan, options)
  const imageSourceEvidence = isImageSourceEvidence(remoteTrace.imageSourceEvidence)
    ? remoteTrace.imageSourceEvidence
    : undefined
  const rawGeneratedUiVerificationSurface = isGeneratedUiVerificationSurface(remoteTrace.generatedUiVerificationSurface)
    ? remoteTrace.generatedUiVerificationSurface
    : undefined
  const generatedUiVerificationSurface = rawGeneratedUiVerificationSurface && imageSourceEvidence && !rawGeneratedUiVerificationSurface.sourceEvidence
    ? { ...rawGeneratedUiVerificationSurface, sourceEvidence: imageSourceEvidence }
    : rawGeneratedUiVerificationSurface
  const generatedUiVerificationPolicy = isGeneratedUiVerificationPolicy(remoteTrace.generatedUiVerificationPolicy)
    ? remoteTrace.generatedUiVerificationPolicy
    : generatedUiVerificationSurface
      ? buildGeneratedUiVerificationPolicy(generatedUiVerificationSurface)
      : undefined
  const retrievalFallbackReason = remoteTrace.retrievalFallbackReason
    ?? remoteTrace.reason
    ?? fallback.retrievalFallbackReason

  return {
    ...fallback,
    source: options.preserveExistingSource
      ? remoteTrace.source ?? options.source ?? fallback.source
      : options.source ?? remoteTrace.source ?? fallback.source,
    generatedAt: remoteTrace.generatedAt ?? fallback.generatedAt,
    mode: isMode(remoteTrace.mode) ? remoteTrace.mode : fallback.mode,
    embeddingUsed: remoteTrace.embeddingUsed === true,
    ...(remoteTrace.embeddingModel ? { embeddingModel: remoteTrace.embeddingModel } : {}),
    ...(typeof remoteTrace.embeddingDimension === 'number' ? { embeddingDimension: remoteTrace.embeddingDimension } : {}),
    embeddingSimilarityTopK: remoteTrace.embeddingSimilarityTopK ?? fallback.embeddingSimilarityTopK,
    structuralCandidateCount: remoteTrace.structuralCandidateCount ?? fallback.structuralCandidateCount,
    vectorCandidateCount: remoteTrace.vectorCandidateCount ?? fallback.vectorCandidateCount,
    admittedCandidateCount,
    ...(selectedOntologySubject ? { selectedOntologySubject } : {}),
    selectedOntologySubjects: selectedOntologySubjects.length > 0
      ? selectedOntologySubjects
      : fallback.selectedOntologySubjects,
    readbackPassed: remoteTrace.readbackPassed ?? (admittedCandidateCount > 0),
    readbackSubjectIds: readbackSubjectIds.length > 0 ? readbackSubjectIds : fallback.readbackSubjectIds,
    fallbackUsed: remoteTrace.fallbackUsed ?? fallback.fallbackUsed,
    ...(retrievalFallbackReason !== undefined ? { retrievalFallbackReason } : {}),
    vectorOnlyHitRejected: remoteTrace.vectorOnlyHitRejected ?? false,
    lowSimilarityFallback: remoteTrace.lowSimilarityFallback ?? false,
    items: items.length > 0 ? items : fallback.items,
    ...(actionFlowOntologyPlan ? { actionFlowOntologyPlan } : {}),
    ...(ontologyMatchingPlan ? { ontologyMatchingPlan } : {}),
    ontologyCompilerContract,
    ...(imageSourceEvidence ? { imageSourceEvidence } : {}),
    ...(generatedUiVerificationSurface ? { generatedUiVerificationSurface } : {}),
    ...(generatedUiVerificationPolicy ? { generatedUiVerificationPolicy } : {}),
  }
}

export function validateStudioOntologyRetrievalTrace(
  trace: StudioOntologyRetrievalTrace | undefined,
): StudioOntologyRetrievalValidation {
  const issues: StudioOntologyRetrievalIssue[] = []
  const add = (code: StudioOntologyRetrievalIssueCode, message: string, severity: 'error' | 'warning' = 'error') => {
    issues.push({ code, severity, message })
  }

  if (!trace) {
    add('missing-ontology-retrieval-trace', 'Studio generation must preserve ontology retrieval trace evidence.')
    return { valid: false, issues }
  }

  if (trace.structuralCandidateCount <= 0) {
    add('structural-candidates-missing', 'Ontology retrieval must start from SPARQL/structural candidates.')
  }
  if (trace.admittedCandidateCount <= 0) {
    add('admitted-candidates-missing', 'Ontology retrieval must admit at least one candidate before generation.')
  }
  if (!trace.readbackPassed || trace.readbackSubjectIds.length === 0) {
    add('readback-missing', 'Ontology retrieval must read back selected subjects before generation.')
  }

  const imageSourceEvidence = trace.imageSourceEvidence
  if (
    imageSourceEvidence &&
    imageSourceEvidence.structuredSourceAvailable === false &&
    imageSourceEvidence.promotion.status !== 'candidate-review-required'
  ) {
    add(
      'image-source-evidence-promotion-mismatch',
      'Image-derived application source must remain candidate-review-required until admitted by review gates.',
    )
  }

  const generatedUiVerificationSurface = trace.generatedUiVerificationSurface
  const generatedUiVerificationPolicy = trace.generatedUiVerificationPolicy
  if (generatedUiVerificationSurface && !generatedUiVerificationPolicy) {
    add(
      'generated-ui-verification-policy-missing',
      'Generated UI verification surface must carry a provenance-driven verification policy.',
    )
  }
  if (generatedUiVerificationPolicy) {
    if (
      generatedUiVerificationSurface &&
      generatedUiVerificationPolicy.provenanceTier !== normalizeGeneratedUiProvenanceTier(
        generatedUiVerificationSurface.provenanceTier,
      )
    ) {
      add(
        'generated-ui-verification-policy-mismatch',
        'Generated UI verification policy provenance tier must match the verified surface.',
      )
    }
    if (
      generatedUiVerificationPolicy.provenanceTier === 'image-derived-candidate' &&
      generatedUiVerificationPolicy.promotionAllowed
    ) {
      add(
        'image-source-evidence-promotion-mismatch',
        'Image-derived candidate verification policy cannot allow direct promotion.',
      )
    }
    if (
      generatedUiVerificationPolicy.provenanceTier === 'image-derived-candidate' &&
      !generatedUiVerificationSurface?.sourceEvidence &&
      !imageSourceEvidence
    ) {
      add(
        'image-source-evidence-promotion-mismatch',
        'Image-derived candidate verification requires image source evidence.',
      )
    }
  }
  if (generatedUiVerificationSurface) {
    const effectivePolicy = generatedUiVerificationPolicy
      ?? buildGeneratedUiVerificationPolicy(generatedUiVerificationSurface)
    const surfaceValidation = validateGeneratedUiVerificationSurface(
      generatedUiVerificationSurface,
      effectivePolicy,
    )
    for (const issue of surfaceValidation.issues) {
      add(
        issue.includes('hidden truths')
          ? 'image-source-hidden-truth-inferred'
          : 'generated-ui-verification-blocked',
        issue,
      )
    }
  }

  if (!trace.ontologyMatchingPlan || trace.ontologyMatchingPlan.decisions.length === 0) {
    add(
      'ontology-matching-plan-missing',
      'Ontology retrieval must include the deterministic intent-to-ontology matching plan.',
      'error',
    )
  } else {
    const blocked = trace.ontologyMatchingPlan.decisions.filter((decision) => decision.generationAllowed === false)
    if (blocked.length > 0) {
      add(
        'ontology-matching-plan-blocked',
        `Ontology matching admitted candidate-only gaps: ${blocked.map((decision) => `${decision.kind}:${decision.requirementId}`).join(', ')}.`,
      )
    }
    const requiredValidation = trace.ontologyMatchingPlan.actionFlowImpact?.requiredValidation ?? []
    if (
      trace.ontologyMatchingPlan.actionFlowImpact?.required === true &&
      requiredValidation.includes('validateActionFlowTopologyContract') &&
      !trace.actionFlowOntologyPlan
    ) {
      add(
        'ontology-matching-actionflow-validation-missing',
        'Ontology matching changed action-bearing assets and must carry ActionFlow ontology planning evidence.',
      )
    }
  }

  const compilerContract = trace.ontologyCompilerContract
  if (!compilerContract) {
    add(
      'ontology-compiler-contract-missing',
      'Ontology retrieval must include the tool-enforced compiler contract before template conversion work can be trusted.',
    )
  } else {
    if (compilerContract.authority !== 'tool') {
      add(
        'ontology-compiler-contract-authority-mismatch',
        'Ontology compiler contract authority must be tool, not model-written.',
      )
    }
    if (compilerContract.status === 'blocked') {
      add(
        'ontology-compiler-contract-blocked',
        compilerContract.reasons[0] ?? 'Ontology compiler contract is blocked.',
      )
    }
    const hardConstraints = new Set(trace.ontologyMatchingPlan?.hardConstraints ?? [])
    if (
      compilerContract.selectedDesignSystemId &&
      !hardConstraints.has(`design-system:${compilerContract.selectedDesignSystemId}:hard`)
    ) {
      add(
        'ontology-compiler-contract-hard-constraint-missing',
        `Selected design system ${compilerContract.selectedDesignSystemId} is not enforced as a hard matching constraint.`,
      )
    }
    if (
      compilerContract.selectedTemplateId &&
      !hardConstraints.has(`template:${compilerContract.selectedTemplateId}:hard`)
    ) {
      add(
        'ontology-compiler-contract-hard-constraint-missing',
        `Selected template ${compilerContract.selectedTemplateId} is not enforced as a hard matching constraint.`,
      )
    }
  }

  if (trace.embeddingUsed) {
    if (trace.embeddingModel !== STUDIO_EXPECTED_EMBEDDING_MODEL) {
      add('vector-model-mismatch', `Expected ${STUDIO_EXPECTED_EMBEDDING_MODEL} embedding evidence.`)
    }
    if (trace.embeddingDimension !== STUDIO_EXPECTED_EMBEDDING_DIMENSION) {
      add('vector-dimension-mismatch', `Expected ${STUDIO_EXPECTED_EMBEDDING_DIMENSION}-dimension embedding evidence.`)
    }
    if (trace.vectorCandidateCount <= 0 || trace.embeddingSimilarityTopK <= 0) {
      add('vector-used-without-candidates', 'Vector rerank was marked used but no vector candidates were recorded.')
    }
    if (!trace.readbackPassed || trace.readbackSubjectIds.length === 0) {
      add('vector-readback-missing', 'Vector hits are not generation evidence until ontology readback passes.')
    }
  }

  if (trace.vectorOnlyHitRejected && trace.admittedCandidateCount <= 0) {
    add('vector-only-hit-admitted', 'Vector-only hits must be rejected and cannot be the only admitted generation evidence.')
  }

  const actionFlowOntologyPlan = trace.actionFlowOntologyPlan
  if (actionFlowOntologyPlan?.publishBlocked) {
    for (const gap of actionFlowOntologyPlan.gaps.filter((item) => item.severity === 'error')) {
      add(
        'actionflow-ontology-gap',
        `ActionFlow ontology ${gap.code}: ${gap.message}`,
        'error',
      )
    }
  }

  return {
    valid: !issues.some((issue) => issue.severity === 'error'),
    issues,
  }
}

export function withStudioOntologyRetrievalTrace(
  appSpec: AppSpecFull,
  options: BuildStudioOntologyRetrievalTraceOptions = {},
): AppSpecFull {
  return {
    ...appSpec,
    ontologyRetrievalTrace: appSpec.ontologyRetrievalTrace
      ? normalizeStudioOntologyRetrievalTrace(appSpec.ontologyRetrievalTrace, appSpec, options)
      : buildStudioOntologyRetrievalTrace(appSpec, options),
  }
}
